/**
 * The Recommendations the Design names (PRD Recommendations 1-9), plus the
 * advisory half of the split custom-field-label rule.
 *
 * payment_provider_missing is not here: "connected" is answered by
 * PaymentServices.getEntityProviders, and rules are synchronous against a
 * snapshot. It needs the provider state resolved onto the store first.
 */
import type { GroupField } from "@/util/methods";
import type {
  BuilderContext,
  ContextElement,
  RuleResult,
  ValidationRule,
} from "../types";

const ALL_ASSETS: ValidationRule["assetTypes"] = ["form", "survey", "quiz"];
const PAYMENT_ASSETS: ValidationRule["assetTypes"] = ["form", "survey"];
const QUIZ_ONLY: ValidationRule["assetTypes"] = ["quiz"];

const text = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

/**
 * Element labels are rich text, so the raw value carries markup that would
 * render literally inside a recommendation sentence. Strip the tags and cap the
 * length so one long question cannot push the panel wider.
 */
const plainLabel = (value: unknown): string => {
  const stripped = text(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return stripped.length > 40 ? `${stripped.slice(0, 40)}\u2026` : stripped;
};

const elementResult = (
  ruleId: string,
  section: ValidationRule["section"],
  entry: ContextElement,
  params?: Record<string, string | number>
): RuleResult => ({
  ruleId,
  severity: "recommendation",
  section,
  key: `${ruleId}:${entry.elementKey}`,
  params,
  target: {
    elementIndex: entry.elementIndex,
    slideIndex: entry.slideIndex,
    elementKey: entry.elementKey,
  },
});

const settingResult = (
  ruleId: string,
  section: ValidationRule["section"],
  settingsTab: string,
  params?: Record<string, string | number>
): RuleResult => ({
  ruleId,
  severity: "recommendation",
  section,
  key: ruleId,
  params,
  target: { settingsTab },
});

const hasElementWithTag = (ctx: BuilderContext, tag: string): boolean =>
  ctx.elements.some((entry) => entry.element.tag === tag);

/* ── Legal and spam ─────────────────────────────────────────────────────── */

export const termsConditionsMissing: ValidationRule = {
  id: "terms_conditions_missing",
  severity: "recommendation",
  assetTypes: ALL_ASSETS,
  section: "fields",
  evaluate: (ctx) =>
    hasElementWithTag(ctx, "terms_and_conditions")
      ? []
      : [
          {
            ...settingResult("terms_conditions_missing", "fields", "elements"),
            target: { settingsTab: "elements", quickAddTag: "terms_and_conditions" },
          },
        ],
};

export const captchaMissing: ValidationRule = {
  id: "captcha_missing",
  severity: "recommendation",
  assetTypes: ALL_ASSETS,
  section: "settings",
  evaluate: (ctx) =>
    hasElementWithTag(ctx, "captcha")
      ? []
      : [
          {
            ...settingResult("captcha_missing", "settings", "elements"),
            target: { settingsTab: "elements", quickAddTag: "captcha" },
          },
        ],
};

/**
 * Dropping a phone field auto-appends a consent block whose Privacy Policy and
 * Terms of Service links both point at example.com. They live in the same
 * element, so one finding covers both — they cannot be fixed independently.
 */
export const privacyPolicyUrlMissing: ValidationRule = {
  id: "privacy_policy_url_missing",
  severity: "recommendation",
  assetTypes: ALL_ASSETS,
  section: "fields",
  evaluate: (ctx) =>
    ctx.elements
      .filter((entry) => text(entry.element.label).includes("www.example.com"))
      .map((entry) =>
        elementResult("privacy_policy_url_missing", "fields", entry)
      ),
};

/* ── Payments ───────────────────────────────────────────────────────────── */

export const sellProductsNotLive: ValidationRule = {
  id: "sell_products_not_live",
  severity: "recommendation",
  assetTypes: PAYMENT_ASSETS,
  section: "payments",
  evaluate: (ctx) => {
    // Live mode is a Sell Products setting, and that panel only opens once its
    // element is selected — so the finding points at the element, not the tab.
    const entry = ctx.elements.find(({ element }) => element.tag === "payment");
    if (!entry) return [];
    return ctx.payment.liveModeOn === true
      ? []
      : [elementResult("sell_products_not_live", "payments", entry)];
  },
};

/* ── Field quality ──────────────────────────────────────────────────────── */

/**
 * Labels generated at drop time end with the element count and a three
 * character base36 code, e.g. "Radio 34q02". The same pattern the builder's own
 * refreshLabelThreeCharCode uses to recognise them.
 */
const GENERATED_LABEL = /(\d+)[a-z0-9]{3}$/i;

export const defaultCustomFieldName: ValidationRule = {
  id: "default_custom_field_name",
  severity: "recommendation",
  assetTypes: ALL_ASSETS,
  section: "fields",
  evaluate: (ctx) =>
    ctx.elements
      .filter((entry) => entry.element.custom)
      .filter((entry) => GENERATED_LABEL.test(text(entry.element.label)))
      .map((entry) =>
        elementResult("default_custom_field_name", "fields", entry, {
          label: text(entry.element.label),
        })
      ),
};

/** The advisory half of the split: the field exists, so its name is frozen. */
export const emptyCustomFieldLabelExisting: ValidationRule = {
  id: "empty_custom_field_label_existing",
  severity: "recommendation",
  assetTypes: ALL_ASSETS,
  section: "fields",
  evaluate: (ctx) =>
    ctx.elements
      .filter((entry) => entry.element.custom && entry.element.customEdited)
      .filter((entry) => !text(entry.element.label))
      .map((entry) =>
        elementResult("empty_custom_field_label_existing", "fields", entry)
      ),
};

/* ── Quiz ───────────────────────────────────────────────────────────────── */

const OPTION_PLACEHOLDER = /^option\s*\d+$/i;

const optionLabels = (element: GroupField): string[] => {
  const options = element.picklistOptions;
  if (!Array.isArray(options)) return [];
  return options.map((option) =>
    text(typeof option === "string" ? option : option?.label)
  );
};

/** Option names are edited on the sidebar's Options tab, not Content. */
const quizOptionResult = (entry: ContextElement): RuleResult => {
  const result = elementResult("quiz_option_placeholder", "questionsPage", entry, {
    label: plainLabel(entry.element.label),
  });
  return { ...result, target: { ...result.target, sidebarTab: "Options" } };
};

export const quizOptionPlaceholder: ValidationRule = {
  id: "quiz_option_placeholder",
  severity: "recommendation",
  assetTypes: QUIZ_ONLY,
  section: "questionsPage",
  evaluate: (ctx) =>
    ctx.elements
      .filter((entry) =>
        optionLabels(entry.element).some((label) =>
          OPTION_PLACEHOLDER.test(label)
        )
      )
      .map((entry) =>
        quizOptionResult(entry)
      ),
};

/**
 * A null or unset score, never 0 — zero is a deliberate answer weighting and
 * must not be flagged.
 */
export const quizScoreBlank: ValidationRule = {
  id: "quiz_score_blank",
  severity: "recommendation",
  assetTypes: QUIZ_ONLY,
  section: "questionsPage",
  evaluate: (ctx) =>
    ctx.elements
      .filter((entry) => {
        const byCategory = (
          entry.element as { scoreByCategory?: Record<string, unknown> }
        ).scoreByCategory;
        if (!byCategory) return false;
        return Object.values(byCategory).some((entryValue) => {
          const score = (entryValue as { score?: unknown })?.score;
          return score === null || score === undefined || score === "";
        });
      })
      .map((entry) => elementResult("quiz_score_blank", "questionsPage", entry)),
};

/**
 * Every CTA on the results page, paired with the destination it points at.
 *
 * Walked from the button text rather than the links, because clearing a link in
 * the results builder drops its key entirely — reading the links alone makes a
 * CTA with no destination look like no CTA at all, which is the one case worth
 * reporting.
 *
 * Saving the results page moves the CTA into `sections` and writes
 * `ctaSettings: null`, so a template that has been saved once has to be read
 * from there. The older top-level shape is still accepted for templates that
 * have never been opened, and both carry the same inner config.
 */
interface CtaEntry {
  label: string;
  link: string;
}

interface CtaConfig {
  dynamicContent?: boolean;
  category?: string;
  buttonText?: Record<string, Record<string, string>>;
  buttonLink?: Record<string, Record<string, string>>;
  staticContent?: { buttonText?: string; buttonLink?: string };
}

/** A results page may hold several CTA sections, so every one is collected. */
const ctaConfigs = (ctx: BuilderContext): CtaConfig[] => {
  const template = ctx.resultTemplate as
    | { ctaSettings?: CtaConfig | null; sections?: unknown }
    | null
    | undefined;

  const sections = Array.isArray(template?.sections) ? template.sections : [];
  const ctaSections = sections.filter(
    (section) => (section as { type?: string })?.type === "ctaSettings"
  );

  // A template that uses sections is read only from them, never from the
  // legacy key — which a save leaves as null — even when every CTA is hidden.
  if (ctaSections.length) {
    return ctaSections
      // The results page renders a section only when it is not hidden, so a
      // hidden CTA shows no button to anyone. Reporting it asks the user to
      // fill in a link for a block they deliberately took down, and the field
      // to do it in is not on screen.
      .filter((section) => !(section as { hide?: boolean })?.hide)
      .map((section) => ((section as { config?: CtaConfig })?.config ?? {}));
  }

  return template?.ctaSettings ? [template.ctaSettings] : [];
};

interface ResultTier {
  id?: string;
  label?: string;
}

const resultTiers = (ctx: BuilderContext): ResultTier[] => {
  const tiers = (ctx.resultTemplate as { tiers?: unknown })?.tiers;
  return Array.isArray(tiers) ? (tiers as ResultTier[]) : [];
};

/**
 * The one CTA button the results builder puts on screen.
 *
 * In dynamic mode the saved config holds a link per tier, and the live quiz
 * shows whichever matches the respondent's score — but the builder previews a
 * single one, picked by a score it keeps in memory and never saves. The first
 * tier is that button: the preview score defaults into the lowest band.
 *
 * Resolved the way CTAPreview does it, label fallback included: with no
 * `category` set, that component reads the map keyed by tier *label* rather
 * than by id, and templates carry both.
 */
const previewedCtaEntry = (
  cta: CtaConfig,
  tier: ResultTier
): CtaEntry => {
  const pick = (map?: Record<string, unknown>): string => {
    const byCategory = map?.[cta.category as string];
    const value =
      byCategory !== undefined
        ? (byCategory as Record<string, unknown>)?.[tier.id as string]
        : map?.[tier.label as string];
    return text(value);
  };

  return { label: pick(cta.buttonText), link: pick(cta.buttonLink) };
};

const ctaEntries = (ctx: BuilderContext): CtaEntry[] => {
  const [firstTier] = resultTiers(ctx);

  return ctaConfigs(ctx).flatMap((cta) => {
    // `dynamicContent` is the switch in the CTA sidebar: off, it edits a single
    // static pair, and that pair is the button. Both halves are always present
    // in a saved config, so the flag is the only thing that says which is live.
    if (cta.dynamicContent === false) {
      return [
        {
          label: text(cta.staticContent?.buttonText),
          link: text(cta.staticContent?.buttonLink),
        },
      ];
    }

    return firstTier ? [previewedCtaEntry(cta, firstTier)] : [];
  });
};

export const quizCtaUrlPlaceholder: ValidationRule = {
  id: "quiz_cta_url_placeholder",
  severity: "recommendation",
  assetTypes: QUIZ_ONLY,
  section: "resultsPage",
  evaluate: (ctx) => {
    const usesPlaceholder = ctaEntries(ctx).some((entry) =>
      entry.link.includes("example.com")
    );
    return usesPlaceholder
      ? [settingResult("quiz_cta_url_placeholder", "resultsPage", "results")]
      : [];
  },
};

export const designRecommendationRules: ValidationRule[] = [
  termsConditionsMissing,
  captchaMissing,
  privacyPolicyUrlMissing,
  sellProductsNotLive,
  defaultCustomFieldName,
  emptyCustomFieldLabelExisting,
  quizOptionPlaceholder,
  quizScoreBlank,
  quizCtaUrlPlaceholder,
];
